<template>
  <div class="ct" :class="{ 'ct--patient': viewerType === 'patient' }">
    <div v-if="loading && comments.length === 0" class="ct-state">
      <i class="fa fa-circle-o-notch fa-spin"></i> Loading conversation…
    </div>

    <div v-else-if="error" class="ct-state ct-state-error">
      <i class="fa fa-exclamation-circle"></i> {{ error }}
    </div>

    <div v-else-if="comments.length === 0" class="ct-empty">
      <slot name="empty">
        <i class="fa fa-comment-o"></i>
        <p v-if="viewerType === 'patient'">
          Have a question? You'll be able to ask your dental office here soon.
        </p>
        <p v-else>
          No messages yet on this plan.
        </p>
      </slot>
    </div>

    <div v-else ref="messages" class="ct-messages">
      <div
        v-for="msg in comments"
        :key="msg.commentID"
        class="ct-msg"
        :class="[msgSideClass(msg), { 'ct-msg-held': isHeldForReview(msg) }]"
      >
        <div class="ct-msg-meta">
          <span class="ct-msg-author">{{ authorLabel(msg) }}</span>
          <span v-if="showAiBadge(msg)" class="ct-msg-ai-badge" title="AI-generated reply">AI</span>
          <!-- Slice 8d-1 — pending-review tag, only on held AI rows in admin view. -->
          <span v-if="isHeldForReview(msg)" class="ct-msg-held-badge" title="Awaiting your approval">
            Pending your review
          </span>
          <span v-if="!isPlaceholder(msg)" class="ct-msg-time">{{ formatTime(msg.createdAt) }}</span>
          <!-- Slice 8d-1 — Approve button releases a held AI draft to the patient.
               Slice 8 UX — shows spinner + disabled when this row is being released. -->
          <button
            v-if="canApproveAi(msg)"
            class="ct-msg-approve"
            type="button"
            :disabled="isApproving(msg)"
            @click="$emit('approve-ai', { commentID: msg.commentID })"
            :title="isApproving(msg) ? 'Sending to patient…' : 'Send this AI draft to the patient as-is'"
          >
            <i :class="isApproving(msg) ? 'fa fa-circle-o-notch fa-spin' : 'fa fa-check'"></i>
            {{ isApproving(msg) ? 'Sending…' : 'Approve &amp; send' }}
          </button>
          <!-- Slice 8c — admin viewer can override an AI reply. We hide the
               action on placeholder rows, on rows already being overridden,
               and on read-by-patient AI rows (override would just append a
               new row, so the button would mislead). Disabled while the same
               row is being approved (avoid mid-release race). -->
          <button
            v-if="canReplaceAi(msg)"
            class="ct-msg-replace"
            type="button"
            :disabled="isApproving(msg)"
            @click="$emit('replace-ai', { commentID: msg.commentID })"
            :title="isHeldForReview(msg) ? 'Edit this AI draft, then send to the patient' : 'Replace this AI draft with your own reply'"
          >
            <i class="fa fa-pencil"></i> {{ isHeldForReview(msg) ? 'Edit' : 'Replace' }}
          </button>
        </div>
        <div class="ct-msg-bubble" :class="{ 'ct-msg-bubble-typing': isPlaceholder(msg), 'ct-msg-bubble-held': isHeldForReview(msg) }">
          <span v-if="isPlaceholder(msg)" class="ct-typing">
            <span class="ct-typing-dot"></span>
            <span class="ct-typing-dot"></span>
            <span class="ct-typing-dot"></span>
            <span class="ct-typing-label">{{ msg._placeholderText || 'Replying…' }}</span>
          </span>
          <template v-else>{{ msg.body }}</template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';

// Cache moment-timezone's known zone list as a Set for fast validation.
// We accept undefined/empty timezone as "use browser local" so a missing
// GENERAL_TIMEZONE doesn't crash the render — it just shows browser local
// like the old behavior.

/**
 * Read-only thread renderer for Slice 8a.
 *
 * Props:
 *   comments       Array of { commentID, author, tcEmployeeNum, body, createdAt, ... }
 *   viewerType     'admin' (Builder, Dashboard) | 'patient' (TxPatientView)
 *                  - admin sees AI replies with an [AI] badge so the TC knows
 *                    they can override
 *                  - patient sees AI and office both rendered as "Office" with
 *                    no badge
 *   loading        Bool — whether a load is in flight (only relevant if no
 *                  comments yet; subsequent polls are silent)
 *   error          String — populated if the load failed
 *   tcLookup       Optional { [employeeNum]: { initials, fName, lName } }
 *                  for resolving Author=office labels on the admin side.
 *                  Patient view never needs this.
 *
 * Compose box / TC override are deliberately left out of 8a — they land in
 * 8b/8c respectively. This component is purely render.
 */
export default {
  name: 'CommentsThread',
  props: {
    comments: { type: Array, default: () => [] },
    viewerType: { type: String, default: 'admin' }, // 'admin' | 'patient'
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    tcLookup: { type: Object, default: () => ({}) },
    /**
     * IANA timezone string (e.g., "America/New_York"). All timestamps in
     * the thread are emitted by the backend as UTC ISO strings; we convert
     * to this zone for display so patients + TCs always see the practice's
     * local time. Empty string → fall through to browser local time.
     */
    timezone: { type: String, default: '' },
    /**
     * Slice 8 UX — commentID currently being released via releaseAiReply
     * (admin Approve flow). When set, the matching AI row's Approve button
     * shows a spinner and both Approve + Edit are disabled so a panicky
     * TC can't fire two requests in a row.
     */
    approvingCommentID: { type: [Number, String], default: null },
  },
  watch: {
    // Auto-scroll the message list to the bottom when new comments arrive
    // (incl. optimistic placeholders — the patient should see their own
    // bubble + "Office is replying…" pop into view immediately, not stuck
     // above the visible window of an overflow:auto container).
    'comments.length': {
      handler() {
        this.$nextTick(() => {
          const el = this.$refs.messages;
          if (el) el.scrollTop = el.scrollHeight;
        });
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Author label depends on which side is reading:
     *   patient view: 'patient' = "You", 'ai' / 'office' = "Office"
     *   admin view:   'patient' = patient's name (or "Patient"),
     *                 'ai' / 'office' = TC initials if known, else "Office"
     */
    authorLabel(msg) {
      if (this.viewerType === 'patient') {
        if (msg.author === 'patient') return 'You';
        return 'Office';
      }
      // admin viewer
      if (msg.author === 'patient') return 'Patient';
      if (msg.author === 'office' && msg.tcEmployeeNum && this.tcLookup[msg.tcEmployeeNum]) {
        const tc = this.tcLookup[msg.tcEmployeeNum];
        return tc.initials || `${(tc.fName || '').charAt(0)}${(tc.lName || '').charAt(0)}` || 'Office';
      }
      return 'Office';
    },

    showAiBadge(msg) {
      // Only the admin sees the [AI] badge — patient sees AI as plain "Office".
      // Placeholder rows (Slice 8b "Office is replying…") never show the badge,
      // since the patient shouldn't be told an AI is drafting before we know.
      if (msg._pending) return false;
      return this.viewerType === 'admin' && msg.author === 'ai';
    },

    isPlaceholder(msg) {
      return !!(msg && msg._pending && msg._placeholderText);
    },

    /**
     * Slice 8d-1 — the row is an AI draft held for office review (admin sees
     * it; patient view filters it out server-side via VisibleToPatientAt
     * IS NULL). Held rows get amber styling, a "Pending your review" tag,
     * and an Approve button.
     */
    isHeldForReview(msg) {
      if (this.viewerType !== 'admin') return false;
      if (!msg || msg.author !== 'ai') return false;
      if (msg._pending) return false;
      // visibleToPatientAt being null/undefined = not yet released. Patient
      // never sees these (filtered server-side); admin sees them as pending.
      return !msg.visibleToPatientAt;
    },

    /**
     * Slice 8d-1 — show the Approve action on AI rows that are held for
     * review. Same gates as isHeldForReview plus we hide it during the
     * brief moment a release request is in flight (handled by parent via
     * the row vanishing from .ct-msg-held once the response lands).
     */
    canApproveAi(msg) {
      return this.isHeldForReview(msg);
    },

    /**
     * Slice 8 UX — true when releaseAiReply is in flight for this row.
     * Used to swap the Approve icon to a spinner and disable both
     * Approve + Edit so the TC can't double-fire the request.
     */
    isApproving(msg) {
      if (!msg || this.approvingCommentID == null) return false;
      return Number(msg.commentID) === Number(this.approvingCommentID);
    },

    /**
     * Slice 8c — show the "Replace" action on an AI row when the TC can
     * cleanly override it without leaving an orphan visible to the patient.
     * Three valid cases:
     *   - admin viewer + Author='ai' + not pending + patient hasn't read
     *     (auto mode — TC has a brief window to override before patient sees)
     *   - admin viewer + Author='ai' + held for review
     *     (review mode — patient hasn't seen it; override is the "edit and
     *     send" shortcut)
     */
    canReplaceAi(msg) {
      if (this.viewerType !== 'admin') return false;
      if (!msg || msg.author !== 'ai') return false;
      if (msg._pending) return false;
      // Held-for-review rows are always replaceable (patient hasn't seen yet).
      if (this.isHeldForReview(msg)) return true;
      // Auto-mode: only if patient hasn't read.
      if (msg.readByPatientAt) return false;
      return true;
    },

    /**
     * "Mine" goes on the right, "theirs" on the left. Flipped by viewerType.
     */
    msgSideClass(msg) {
      const mine = this.viewerType === 'patient'
        ? msg.author === 'patient'
        : (msg.author === 'office' || msg.author === 'ai');
      return mine ? 'ct-msg-mine' : 'ct-msg-theirs';
    },

    formatTime(ts) {
      if (!ts) return '';
      // Backend emits UTC ISO. Parse with that assumption, then convert to
      // the practice's timezone for display. moment.tz with a falsy zone
      // returns Invalid Date, so guard the zone first.
      const tz = this.timezone && moment.tz.zone(this.timezone) ? this.timezone : null;
      const m = tz ? moment.utc(ts).tz(tz) : moment(ts);
      if (!m.isValid()) return '';
      const today = tz ? moment.tz(tz) : moment();
      if (m.isSame(today, 'day')) return m.format('h:mm A');
      if (m.isSame(today.clone().subtract(1, 'day'), 'day')) return `Yesterday ${m.format('h:mm A')}`;
      return m.format('MMM D, h:mm A');
    },
  },
};
</script>

<style scoped>
.ct {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  font-size: 14px;
}

.ct-state {
  padding: 32px 24px;
  text-align: center;
  color: #6b7785;
}
.ct-state-error { color: #dc2626; }
.ct-state .fa { margin-right: 6px; }

.ct-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  color: #94a3b8;
  text-align: center;
}
.ct-empty .fa {
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
}
.ct-empty p {
  margin: 0;
  max-width: 280px;
  line-height: 1.5;
}

.ct-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ct-msg {
  display: flex;
  flex-direction: column;
  max-width: 78%;
}
.ct-msg-mine {
  align-self: flex-end;
  align-items: flex-end;
}
.ct-msg-theirs {
  align-self: flex-start;
  align-items: flex-start;
}

.ct-msg-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
  font-size: 11px;
  color: #6b7785;
}
.ct-msg-author { font-weight: 600; color: #374151; }
.ct-msg-time { color: #94a3b8; }
.ct-msg-ai-badge {
  display: inline-block;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: #ede9fe;
  color: #6d28d9;
  border-radius: 3px;
}

/* Slice 8c — Replace action on AI rows in admin view */
.ct-msg-replace {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  color: #6d28d9;
  background: #fff;
  border: 1px solid #e0d7fe;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.ct-msg-replace:hover { background: #ede9fe; }
.ct-msg-replace .fa { font-size: 9px; }

/* Slice 8d-1 — held-for-review styling. Amber so it visually stands out from
   approved (green) rows; "Pending your review" badge in the meta line. */
.ct-msg-held-badge {
  display: inline-block;
  padding: 1px 7px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 3px;
}
.ct-msg-bubble-held {
  background: #fffbeb !important;
  color: #1f2937 !important;
  border: 1px solid #fde68a !important;
}
.ct-msg-approve {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  background: #16a34a;
  border: 1px solid #16a34a;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.12s ease;
}
.ct-msg-approve:hover:not(:disabled) { background: #15803d; }
.ct-msg-approve:disabled,
.ct-msg-replace:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.ct-msg-approve .fa { font-size: 9px; }

.ct-msg-bubble {
  padding: 10px 14px;
  border-radius: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}
.ct-msg-mine .ct-msg-bubble {
  background: #0f766e;
  color: #fff;
  border-bottom-right-radius: 3px;
}
.ct-msg-theirs .ct-msg-bubble {
  background: #f1f5f9;
  color: #1f2937;
  border-bottom-left-radius: 3px;
}

/* Patient view sits on a cream paper background — give the messages a
   slightly different feel so they don't look out of place. */
.ct--patient .ct-msg-theirs .ct-msg-bubble {
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

/* Slice 8b — typing indicator for the "Office is replying…" placeholder */
.ct-msg-bubble-typing {
  background: #f5f3ff !important;
  color: #4c1d95 !important;
  border: 1px solid #e0d7fe !important;
  padding: 12px 14px !important;
}
.ct-typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.ct-typing-dot {
  width: 6px;
  height: 6px;
  background: #6d28d9;
  border-radius: 50%;
  display: inline-block;
  animation: ct-typing-bounce 1.2s infinite ease-in-out;
}
.ct-typing-dot:nth-child(2) { animation-delay: 0.18s; }
.ct-typing-dot:nth-child(3) { animation-delay: 0.36s; }
.ct-typing-label {
  margin-left: 8px;
  font-size: 12px;
  font-style: italic;
  color: #6d28d9;
}
@keyframes ct-typing-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
  40% { transform: translateY(-4px); opacity: 1; }
}
</style>
